import StateDrivenSlotDropdown from '../../Generic/StateDrivenDropdown';
import FilterDropdownOption from 'components/Functional/Filters/FilterDropdownOption';
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from 'next-localization';
import { useRef } from 'react';
import { Filter } from 'hooks/useFilters';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';

type Props = {
  filter: Filter;
  isFetching: boolean;
  optionSelect: (option, filterName) => void;
  onClearFilterSelection: () => void;
  filterApply: () => void;
  needAggregateCount: () => void;
  resetUnAppliedOptions: (filter) => void;
};

const FilterDropdown = ({
  filter,
  isFetching,
  optionSelect,
  onClearFilterSelection,
  filterApply,
  needAggregateCount,
  resetUnAppliedOptions,
}: Props) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const filterButton = useRef();
  const filterDropdownOptionsWrapper = useRef();

  const { t } = useI18n();

  const selectedOptions = useMemo(() => {
    return filter.options.filter((option) => option.selected);
  }, [filter]);

  const filterOptions = useMemo(() => {
    return filter.options.filter((option) => option.aggregateCount !== null);
  }, [filter]);

  const filterButtonTextColor = useMemo(() => {
    return !selectedOptions.length && !isDropdownOpen ? 'text-grey' : 'text-abrdn-black';
  }, [selectedOptions, isDropdownOpen]);

  const ariaLabel = useMemo(() => {
    return `${filter.displayName}. ${selectedOptions.length} ${t('filtersApplied')}.`;
  }, [selectedOptions, filter, t]);

  const filterPlaceholder = useMemo(() => {
    if (selectedOptions.length) {
      return `${selectedOptions.length} ${
        selectedOptions.length === 1 ? t('filterSelected') : t('filtersSelected')
      }`;
    } else {
      return t('selectFilter');
    }
  }, [selectedOptions, t]);

  const toggleDropdown = (): void => {
    setIsDropdownOpen((prev) => !prev);
    filterButton.current?.focus();
  };

  const selectFilterOptionWithArrow = (direction: string) => {
    const currentFocusedElement = document.activeElement;

    if (direction === 'down') {
      let nextElement = currentFocusedElement?.nextElementSibling;
      while (nextElement && nextElement?.classList.contains('disabled')) {
        nextElement = nextElement?.nextElementSibling;
      }
      if (nextElement) {
        nextElement.focus();
      }
    } else if (direction === 'up') {
      let previousElement = currentFocusedElement?.previousElementSibling;
      while (previousElement && previousElement?.classList.contains('disabled')) {
        previousElement = previousElement?.previousElementSibling;
      }
      if (previousElement) {
        previousElement.focus();
      }
    }
  };

  const selectFirstFilterOption = () => {
    if (!isDropdownOpen) return;
    const firstOption = filterDropdownOptionsWrapper.current.childNodes[0];

    firstOption.focus();
  };

  const handleCancleEvent = () => {
    toggleDropdown();
    onClearFilterSelection(filter);
  };

  const handleConfirmEvent = () => {
    toggleDropdown();
    filterApply();
  };

  useEffect(() => {
    if (isDropdownOpen) {
      needAggregateCount();
    } else {
      resetUnAppliedOptions(filter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownOpen]);

  return (
    <StateDrivenSlotDropdown
      className={`dropdown me-1 ${isDropdownOpen ? 'dropdown--expanded' : ''}`}
      isOpen={isDropdownOpen}
      showActionButtons={true}
      buttonsLayout="justify-content-between"
      onOutsideClick={toggleDropdown}
      onLostFocus={() => setIsDropdownOpen(false)}
      onCancelEvent={handleCancleEvent}
      onConfirmEvent={handleConfirmEvent}
      triggerAreaSlot={
        <div>
          {filter.displayName && (
            <Text tag="p" field={{ value: filter.displayName }} className="form-label mb-1.5" />
          )}
          <button
            ref={filterButton}
            type="button"
            className="d-flex align-items-center filter-selector btn btn-naked"
            aria-expanded={isDropdownOpen ? 'true' : 'false'}
            aria-label={ariaLabel}
            onClick={toggleDropdown}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                selectFirstFilterOption();
              }
            }}
          >
            <span className={`field-text ${filterButtonTextColor}`}>{filterPlaceholder}</span>
            <span className="ms-auto">
              <em className="d-flex bi-chevron-down" aria-hidden="true" />
            </span>
          </button>
        </div>
      }
    >
      <div className="filter-dropdown-window d-flex flex-column position-relative">
        <div
          ref={filterDropdownOptionsWrapper}
          className="filter-dropdown-options-wrapper"
          tabIndex="-1"
        >
          {filterOptions.map((filterOption) => (
            <FilterDropdownOption
              key={filterOption.id}
              isVisible={isDropdownOpen}
              filterOption={filterOption}
              isFetching={isFetching}
              onClick={() => optionSelect(filterOption, filter.name)}
              onKeyDown={() => optionSelect(filterOption, filter.name)}
              selectFilterOptionWithArrow={selectFilterOptionWithArrow}
            />
          ))}
        </div>
      </div>
    </StateDrivenSlotDropdown>
  );
};

export default FilterDropdown;
